import React from 'react';
import { PaymentMethod } from '../../../../types';
import { CreditCardIcon, PixIcon, BoletoIcon } from '../../../icons';
import type { PaymentMethodOption } from '../types/payment-form.types';

interface PaymentMethodSelectorProps {
  selectedMethod: PaymentMethod | '';
  onMethodChange: (method: PaymentMethod) => void;
  error?: string;
  availablePaymentMethods?: string[];
  paymentMethodDiscounts?: {
    PIX?: number;
    CARD?: number;
    BOLETO?: number;
  };
  paymentMethodPrices?: {
    PIX?: number;
    CARD?: number;
    BOLETO?: number;
  };
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  selectedMethod,
  onMethodChange,
  error,
  availablePaymentMethods,
  paymentMethodDiscounts,
  paymentMethodPrices,
}) => {
  const allowed = new Set((availablePaymentMethods || []).map((m) => String(m).toLowerCase()));
  const shouldShow = (method: PaymentMethod): boolean => {
    return allowed.has(String(method).toLowerCase());
  };

  const paymentMethods: PaymentMethodOption[] = [
    ...(shouldShow(PaymentMethod.CREDIT_CARD)
      ? [
          {
            id: PaymentMethod.CREDIT_CARD,
            label: 'Cartão de crédito',
            icon: <CreditCardIcon className="w-5 h-5 mr-2" />,
            discount: paymentMethodDiscounts?.CARD,
            price: paymentMethodPrices?.CARD,
          },
        ]
      : []),
    ...(shouldShow(PaymentMethod.PIX)
      ? [
          {
            id: PaymentMethod.PIX,
            label: 'Pix',
            icon: <PixIcon className="w-5 h-5 mr-2" />,
            discount: paymentMethodDiscounts?.PIX,
            price: paymentMethodPrices?.PIX,
          },
        ]
      : []),
    ...(shouldShow(PaymentMethod.BOLETO)
      ? [
          {
            id: PaymentMethod.BOLETO,
            label: 'Boleto',
            icon: <BoletoIcon className="w-5 h-5 mr-2" />,
            discount: paymentMethodDiscounts?.BOLETO,
            price: paymentMethodPrices?.BOLETO,
          },
        ]
      : []),
  ];

  return (
    <div>
      <label className="block text-sm font-medium text-text-secondary dark:text-gray-400 mb-2">Método de Pagamento</label>
      <div className="flex flex-col space-y-2">
        {paymentMethods.map((method) => (
          <button
            type="button"
            key={method.id}
            onClick={() => onMethodChange(method.id)}
            style={{ borderRadius: 'var(--td-radius, 0.375rem)' }}
            className={`flex items-center justify-between flex-1 p-3 border text-sm font-medium transition-colors relative
              ${
                selectedMethod === method.id
                  ? 'bg-gray-200 text-black ring-2 ring-black ring-offset-1 dark:ring-offset-card-bg-dark'
                  : 'bg-card-bg-light dark:bg-input-bg-dark text-text-primary dark:text-gray-200 border-medium-gray dark:border-gray-600 hover:bg-light-gray dark:hover:bg-gray-700'
              }`}
          >
            <div className="flex items-center">
              {method.icon}
              {method.label}
            </div>
            <div className="flex flex-col items-end gap-1">
              {method.discount !== undefined && method.discount !== null && method.discount > 0 && (
                <span
                  className={`px-2 py-1 text-xs font-bold rounded ${
                    selectedMethod === method.id ? 'bg-green-500 text-white' : 'bg-green-500 text-white'
                  }`}
                >
                  {method.discount}% DE DESCONTO
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default PaymentMethodSelector;
